import React, { useState, useEffect } from "react";
import {
  SVMCard,
  SVMHead,
  SVMTitle,
  Collapse,
  FadeBody,
  Body,
  SubTitle,
  Th,
  Td,
  TableWrap,
  TableTop,
  Table,
  TableScroll,
  Search,
  Skel,
  clamp,
  usageColor,
  formatStorage,
  UsageDisplay,
  UsageBar,
  UsageText,
  VolumnCount,
  VolumeActions,
  ActionButton,
  TableTopRight,
  TopActions,
  TopActionButton,
} from "./styles";

import DeleteVolume from "../components/deleteVolume";
import ResizeVolume from "../components/resizeVolume";
import { storageAPI } from "api";
import { getTaskData } from "api/taskStatus";
import { useTaskStore } from "store/task";

const SVMPanel = (props) => {
  const [selectVolume, setSelectVolume] = useState("");
  const [isResizeVolume, setIsResizeVolume] = useState(false);
  const [isDeleteVolume, setIsDeleteVolume] = useState(false);
  const [svmQ, setSvmQ] = useState("");

  const isLoading = props.loading === true;
  const svm = !isLoading ? props.svm : null;
  const open = !isLoading ? props.open : false;
  const onToggle = !isLoading ? props.onToggle : undefined;

  const pct = clamp(Math.round(props?.summary?.used_percent ?? 0), 0, 100);

  const [data, setData] = useState(null);
  const [volumes, setVolumes] = useState([]);

  const volumeSuccessTick = useTaskStore((s) => s.volumeSuccessTick);

  useEffect(() => {
    let isMounted = true;

    const fetchData = async () => {
      if (!props.svm) return;
      try {
        const r = await getTaskData(() => storageAPI.getSVMsDetail(props.svm));
        if (!isMounted) return;

        if (r?.status === "SUCCESS") {
          const result = r.result;
          setData(result);
          setVolumes(result.volumes ?? []);
        }
      } catch (e) {
        console.error("SVM fetch error:", e);
      }
    };

    fetchData();
    return () => {
      isMounted = false;
    };
  }, [props.svm, volumeSuccessTick]);

  const filteredVolumes = volumes.filter((v) => {
    const q = svmQ.trim().toLowerCase();
    if (!q) return true;
    return (
      v.name.toLowerCase().includes(q) ||
      v.aggr.toLowerCase().includes(q) ||
      v.path.toLowerCase().includes(q)
    );
  });

  return (
    <SVMCard>
      <SVMHead
        $pct={pct}
        onClick={() => {
          if (!data) return;
          if (isDeleteVolume || isResizeVolume) return;
          onToggle?.();
        }}
      >
        <SVMTitle data-click-pass>
          {!data ? (
            <>
              <Skel style={{ width: 180 }} />
              <Skel style={{ width: 100, marginLeft: 8 }} />
            </>
          ) : (
            <>
              {svm}
              <VolumnCount>({volumes.length} volumes)</VolumnCount>
            </>
          )}
        </SVMTitle>

        {isDeleteVolume && (
          <DeleteVolume
            open={isDeleteVolume}
            setOpen={setIsDeleteVolume}
            base={selectVolume}
          />
        )}

        {isResizeVolume && (
          <ResizeVolume
            open={isResizeVolume}
            setOpen={setIsResizeVolume}
            base={selectVolume}
          />
        )}
      </SVMHead>

      {data && (
        <Collapse $open={open}>
          <FadeBody $open={open}>
            <Body>
              <section>
                <SubTitle>Volumes</SubTitle>

                <TableWrap>
                  <TableTop>
                    <Search
                      value={svmQ}
                      onChange={(e) => setSvmQ(e.target.value)}
                      placeholder="Filter volumes"
                    />

                    <TableTopRight>
                      <span style={{ fontSize: 12, color: "#64748b" }}>
                        Total: <b>{filteredVolumes.length}</b>
                      </span>

                      <TopActions>
                        <TopActionButton
                          $type="volume-create"
                          onClick={() => props.onCreateVolume(svm)}
                        >
                          + Volume
                        </TopActionButton>
                        {svm === "oasys_share_svm" && (
                          <>
                            <TopActionButton
                              $type="project-create"
                              onClick={() => props.onCreateProject(true)}
                            >
                              + Project
                            </TopActionButton>

                            <TopActionButton
                              $type="project-resize"
                              onClick={() => props.onResizeProject(true)}
                            >
                              ⚙ Project
                            </TopActionButton>
                          </>
                        )}
                      </TopActions>
                    </TableTopRight>
                  </TableTop>

                  <TableScroll>
                    <Table>
                      <thead>
                        <tr>
                          <Th>Volume</Th>
                          <Th>Path</Th>
                          <Th>Usage</Th>
                          <Th>Size</Th>
                          <Th>Used</Th>
                          <Th>Available</Th>
                          <Th>Aggregate</Th>
                          <Th>Actions</Th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredVolumes.map((v) => {
                          const vpct = clamp(
                            Math.round(v.used_percent),
                            0,
                            100
                          );
                          const vcolor = usageColor(vpct);

                          return (
                            <tr key={v.name}>
                              <Td>{v.name}</Td>
                              <Td>{v.path}</Td>
                              <Td>
                                <UsageDisplay>
                                  <UsageBar $pct={vpct} $color={vcolor} />
                                  <UsageText $color={vcolor}>{vpct}%</UsageText>
                                </UsageDisplay>
                              </Td>
                              <Td>{formatStorage(v.size)}</Td>
                              <Td>{formatStorage(v.used)}</Td>
                              <Td>{formatStorage(v.available)}</Td>
                              <Td>{v.aggr}</Td>
                              <Td>
                                <VolumeActions>
                                  <ActionButton
                                    onClick={() => {
                                      setSelectVolume(v.name);
                                      setIsResizeVolume(true);
                                    }}
                                  >
                                    ⚙️
                                  </ActionButton>
                                  <ActionButton
                                    $type="delete"
                                    onClick={() => {
                                      setSelectVolume(v.name);
                                      setIsDeleteVolume(true);
                                    }}
                                  >
                                    🗑
                                  </ActionButton>
                                </VolumeActions>
                              </Td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </Table>
                  </TableScroll>
                </TableWrap>
              </section>
            </Body>
          </FadeBody>
        </Collapse>
      )}
    </SVMCard>
  );
};

export default SVMPanel;
